// The verification panel: what is being checked, right now, and what it is
// being checked against.
//
// Top to bottom, and the order is the argument: the running counts (this
// much has been checked for you, and it is still climbing), the strip chart
// (here is the checking, happening, slice by slice), the whole video (and
// these are the parts nobody has looked at), then what it is all checked
// against, in a sentence.
//
// WHO IT IS FOR. Not someone who will read a hash. Someone who opens this to
// find out whether to believe the picture, and needs to come away knowing two
// things without understanding either: that a great deal of work is being
// done on their behalf, and that it is being done right now (Gavin and Kai,
// 10 Oct). So the two counts lead, at the largest size in the panel, and
// everything a sceptic would go and check for themselves — the slice under
// the playhead, its fingerprint, the addresses on the chain — is one click
// away behind "Technical details" rather than in the way. Folding it also
// lets the whole panel show on a laptop without scrolling.
//
// THE PANEL NEVER BLOCKS THE PLAYER. Its backdrop takes no pointer events —
// only the panel itself does — so a viewer can scrub, pause and change quality
// while watching. That is not a convenience: seeking is the most persuasive
// thing anyone can do to this panel, because the coverage then visibly grows a
// second island instead of one continuous run, which is the whole "we claim
// only what we checked" argument made without a word of copy.
//
// Consequently there is no click-outside-to-close: the clicks a backdrop would
// have to swallow to detect that are exactly the ones the player needs. Escape
// closes it, and so do the close button and the V itself.
//
// Every colour, size, weight and spacing here comes from verificationTheme.ts;
// nothing in this module hardcodes a hex value.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { color, font, motion, prefersReducedMotion, size, space, weight } from './verificationTheme.js'
import CoverageBar from './chart/CoverageBar.js'
import SliceDetail from './SliceDetail.js'
import VerificationChart from './chart/VerificationChart.js'
import LiveCounter from './LiveCounter.js'
import Provenance, { type ProvenanceProps } from './Provenance.js'
import { fingerprintForWidth, isNarrowPanel, isShortPlayer } from './panelLayout.js'
import type { CompositeSummary } from './ledger/composite.js'
import type { Mismatch } from './ledger/lanes.js'
import { verificationGlyph } from '../player/designPreview.js'

export interface VerificationModalProps {
  /** The read model the chart draws from. Null until a root has verified and
   * lanes exist, which is most of a page's life. */
  summary: CompositeSummary | null
  /**
   * The first slice that disagreed with the seal, if any. Taken from the
   * LaneSet rather than the composite because only the LaneSet keeps both
   * hashes, and stacking them is the whole point of the failure view.
   */
  mismatch?: Mismatch | null
  /** Ref-backed and stable, straight from the player. The chart's frame loop
   * calls these; they are never values, or the chart would re-render on every
   * timeupdate. */
  getTime: () => number
  getBufferedEnd: () => number
  /**
   * Why there is no chart, when there is no chart.
   *
   * There is deliberately no "unanchored" variant: a video with nothing on the
   * chain shows no V and no panel at all (see RealVerifiedPlayer), so this is
   * never reached for one.
   */
  notice?: 'checking'
  /** The chain half of the panel. Absent while it is still being read. */
  provenance?: Omit<ProvenanceProps, 'part' | 'narrow'>
  onClose: () => void
}

/** "218 of 604 slices checked · no mismatches" — a count, never a blanket
 * claim that the video is verified. Nothing is claimed here about the slices
 * that have not loaded. */
// The heading's mark: the control bar's V, or the logo under ?verifylogo=logo.
const headingArt = verificationGlyph()

function sliceLine(summary: CompositeSummary): string {
  const mismatches = summary.mismatchIndex === null ? 'no mismatches' : 'one mismatch'
  return `${summary.checkedCount} of ${summary.totalCount} slices checked · ${mismatches}`
}

/**
 * One running count: what is counted, the figure climbing, and the rate.
 *
 * The rate is per second OF VIDEO, not per second of wall clock. Checking
 * runs in bursts as slices arrive, so a wall-clock rate would swing between
 * nothing and enormous; this one is steady, and it is the figure that answers
 * "how much is there in what I am watching?".
 */
const Count: React.FC<{ label: string; value: number; perSecond: number; frozen: boolean; small: boolean }> = ({
  label,
  value,
  perSecond,
  frozen,
  small,
}) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: size.label, opacity: 0.72 }}>{label}</div>
    <LiveCounter
      value={value}
      frozen={frozen}
      style={{
        display: 'block',
        fontFamily: font.mono,
        fontSize: small ? size.summary : size.count,
        fontWeight: weight.medium,
        lineHeight: 1.25,
        letterSpacing: '-0.01em',
        color: frozen ? color.bone : color.jade,
        whiteSpace: 'nowrap',
      }}
    />
    {perSecond > 0 && (
      <div style={{ fontSize: size.label, opacity: 0.55 }}>
        <span style={{ fontFamily: font.mono, fontSize: size.mono }}>
          {Math.round(perSecond).toLocaleString('en-GB')}
        </span>{' '}
        in every second of video
      </div>
    )}
  </div>
)

/**
 * The two counts side by side: pixels, then bytes.
 *
 * Bytes are what is checked. Pixels are what those bytes turn into, worked
 * out from the picture's size (`pixelsPerSecond`, lanes.ts) — the larger and
 * the more picturable number, so it goes first. Either is left out when its
 * source cannot count it, rather than shown as a zero.
 */
const Counts: React.FC<{ summary: CompositeSummary; frozen: boolean; narrow: boolean; small: boolean }> = ({
  summary,
  frozen,
  narrow,
  small,
}) => {
  const seconds = summary.checkedSeconds
  const rate = (total: number) => (seconds > 0 ? total / seconds : 0)
  if (summary.checkedPixels <= 0 && summary.checkedBytes <= 0) return null
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: narrow ? '1fr' : '1fr 1fr',
        gap: narrow ? space.sm : space.lg,
      }}
    >
      {summary.checkedPixels > 0 && (
        <Count
          label="Pixels checked"
          value={summary.checkedPixels}
          perSecond={rate(summary.checkedPixels)}
          frozen={frozen}
          small={small}
        />
      )}
      {summary.checkedBytes > 0 && (
        <Count
          label="Bytes checked"
          value={summary.checkedBytes}
          perSecond={rate(summary.checkedBytes)}
          frozen={frozen}
          small={small}
        />
      )}
    </div>
  )
}

/** What to say when there is no chart. */
function noticeText(_notice: 'checking'): string {
  return 'Nothing has been checked yet. Slices are checked as their bytes arrive, so this fills in as the video plays.'
}

const Divider: React.FC = () => (
  <div style={{ height: 1, background: color.rule, margin: `${space.lg}px 0` }} />
)

/**
 * The failure block, shown above everything else once a slice has disagreed.
 *
 * THE COPY IS PART OF THE DESIGN and is carried over from
 * VerificationFailure.tsx unchanged in substance. A hash disagreement has two
 * plausible causes and they are wildly unequal in likelihood: a re-host or a
 * re-encode (common, innocent) versus someone altering the video (rare).
 * Leading with tampering would accuse a publisher of something a CDN did, on
 * evidence that does not distinguish the two. So this says what is true — these
 * bytes can no longer be checked against the seal — names the likely cause
 * first, and stops there.
 */
const FailureBlock: React.FC<{ mismatch: Mismatch; narrow: boolean }> = ({ mismatch, narrow }) => (
  <div
    role="alert"
    style={{
      border: `1px solid ${color.alarm}`,
      borderRadius: 2,
      padding: space.md,
      marginBottom: space.lg,
      maxWidth: '72ch',
    }}
  >
    <div style={{ fontSize: size.title, fontWeight: weight.semibold, color: color.alarm }}>
      These bytes no longer match what the publisher sealed.
    </div>
    <p style={{ margin: `${space.sm}px 0 0`, opacity: 0.85 }}>{mismatch.detail}</p>
    <p style={{ margin: `${space.sm}px 0 0`, opacity: 0.85 }}>
      The most likely explanation is an innocent one: videos get re-hosted and re-encoded — moved to a
      different CDN, re-uploaded, repackaged by a storage provider — and any of those changes the bytes
      even though the video looks identical. That is far more common than anyone tampering with it.
    </p>
    <p style={{ margin: `${space.sm}px 0 0`, opacity: 0.85 }}>
      Either way, the seal is what makes this video checkable, and it can no longer be checked. Playback
      stops here rather than continuing unverified.
    </p>

    {(mismatch.expectedHash || mismatch.actualHash) && (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          rowGap: space.xs,
          alignItems: 'baseline',
          marginTop: space.md,
          paddingTop: space.md,
          borderTop: `1px solid ${color.rule}`,
        }}
      >
        <div style={{ opacity: 0.55, paddingRight: space.md }}>Sealed</div>
        <div style={{ fontFamily: font.mono, fontSize: size.mono, wordBreak: 'break-all' }}>
          {mismatch.expectedHash ? fingerprintForWidth(mismatch.expectedHash, narrow) : 'not recorded'}
        </div>
        <div style={{ opacity: 0.55, paddingRight: space.md }}>Received</div>
        <div
          style={{
            fontFamily: font.mono,
            fontSize: size.mono,
            wordBreak: 'break-all',
            color: color.alarm,
          }}
        >
          {mismatch.actualHash ? fingerprintForWidth(mismatch.actualHash, narrow) : 'not recorded'}
        </div>
      </div>
    )}
  </div>
)

const VerificationModal: React.FC<VerificationModalProps> = ({
  summary,
  mismatch = null,
  getTime,
  getBufferedEnd,
  notice = 'checking',
  provenance,
  onClose,
}) => {

  const backdropRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  // Measured, not guessed from the viewport: the panel lives inside the
  // player, and an embedded player can be phone-narrow on a desktop. The
  // backdrop fills the player, so its height is the player's.
  const [panelWidth, setPanelWidth] = useState(0)
  const [playerHeight, setPlayerHeight] = useState(0)

  useLayoutEffect(() => {
    const panel = panelRef.current
    const backdrop = backdropRef.current
    if (!panel || !backdrop) return undefined
    const measure = () => {
      setPanelWidth(panel.clientWidth)
      setPlayerHeight(backdrop.clientHeight)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(panel)
    observer.observe(backdrop)
    return () => observer.disconnect()
  }, [])

  // Focus moves in on open so a keyboard user is inside the panel.
  useEffect(() => {
    panelRef.current?.focus()
  }, [])

  // Escape closes it — but NEVER once a slice has disagreed. A halted player
  // has nothing to go back to, and dismissing the explanation would leave a
  // viewer staring at a stopped video with no account of why.
  useEffect(() => {
    if (mismatch) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mismatch, onClose])

  const frozen = Boolean(mismatch)
  const narrow = isNarrowPanel(panelWidth)
  // Smaller type: a narrow panel, or a short player (a phone on its side).
  // Only `narrow` changes the layout — one column, short fingerprints.
  const small = narrow || isShortPlayer(playerHeight)

  return (
    <div
      ref={backdropRef}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Only the panel itself takes clicks. Everything around it — the video,
        // the scrubber, every player control — stays live underneath.
        pointerEvents: 'none',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="false"
        aria-label="Verification"
        tabIndex={-1}
        style={{
          pointerEvents: 'auto',
          outline: 'none',
          width: 'min(880px, 88%)',
          // No pixel cap: with the room, the whole panel shows and nothing
          // is left to scroll to (Gavin, 9 Oct).
          maxHeight: '88%',
          overflowY: 'auto',
          boxSizing: 'border-box',
          padding: small ? space.md : space.lg,
          borderRadius: space.sm,
          border: `1px solid ${frozen ? color.alarm : color.rule}`,
          // ~88% opaque over a blur, so the video keeps playing behind the panel
          // rather than being replaced by it.
          backgroundColor: `${color.ink}e0`,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          color: color.bone,
          fontFamily: font.prose,
          fontSize: size.body,
          lineHeight: 1.5,
          transition: prefersReducedMotion() ? undefined : `border-color ${motion.failureFadeMs}ms ease`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: space.md }}>
          {/* The FileFidelity heading (Gavin, 16 Oct mock): the V (17 Oct; the logo
              under ?verifylogo=logo), and the
              brand line in its green. The claim in it — "Verified", "as you
              stream" — is made only while slices are being checked and none
              has disagreed; before that, or once one has, the logo says only
              whose check this is. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: small ? space.sm : space.md, minWidth: 0 }}>
            {headingArt.isLogo ? (
              <img
                src="/img/filefidelity-logo.png"
                alt=""
                width={small ? 32 : 44}
                height={small ? 32 : 44}
                style={{ flexShrink: 0, display: 'block' }}
              />
            ) : (
              // The V from the control bar (Gavin, 17 Oct), a silhouette like
              // the button's, so it is stencilled in the brand line's green.
              <span
                aria-hidden="true"
                style={{
                  flexShrink: 0,
                  display: 'block',
                  width: small ? 32 : 44,
                  height: small ? 32 : 44,
                  backgroundColor: color.brand,
                  WebkitMaskImage: `url(${headingArt.image})`,
                  maskImage: `url(${headingArt.image})`,
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                  // The v4 V is wider than tall, so 'contain' leaves room
                  // below it, and a mask repeats by default: a second V's
                  // tips showed under the first (Kai, 19 Oct).
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  maskPosition: 'center',
                }}
              />
            )}
            <div
              style={{
                fontSize: small ? size.summary - 3 : size.brand,
                fontWeight: weight.regular,
                lineHeight: 1.2,
                color: color.brand,
              }}
            >
              {summary && !frozen ? 'FileFidelity Verified - Live verification as you stream' : 'FileFidelity'}
            </div>
          </div>
          {!frozen && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close verification details"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: color.bone,
                fontFamily: font.prose,
                fontSize: size.title,
                lineHeight: 1,
                minWidth: 32,
                minHeight: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.7,
              }}
            >
              ✕
            </button>
          )}
        </div>

        {mismatch && (
          <div style={{ marginTop: space.md }}>
            <FailureBlock mismatch={mismatch} narrow={narrow} />
          </div>
        )}

        {summary ? (
          <>
            <div style={{ marginTop: space.md }}>
              <Counts summary={summary} frozen={frozen} narrow={narrow} small={small} />
            </div>

            <div style={{ marginTop: space.md }}>
              <VerificationChart
                summary={summary}
                getTime={getTime}
                getBufferedEnd={getBufferedEnd}
                frozen={frozen}
                narrow={narrow}
              />
            </div>

            <div style={{ marginTop: space.md }}>
              <div
                style={{
                  display: 'flex',
                  // Stacked on a phone. Side by side, the caption and the count
                  // each wrap to two lines and interleave, which is how this
                  // panel came to look squashed in the first place.
                  flexDirection: narrow ? 'column' : 'row',
                  alignItems: narrow ? 'flex-start' : 'baseline',
                  justifyContent: 'space-between',
                  gap: narrow ? space.xs : space.md,
                  marginBottom: space.sm,
                  fontSize: size.label,
                }}
              >
                {!narrow && (
                  <div style={{ opacity: 0.72 }}>
                    The whole video. Each part is checked as it loads; empty stretches have not loaded yet.
                  </div>
                )}
                <div style={{ fontFamily: font.mono, fontSize: size.mono, fontWeight: weight.regular }}>
                  {sliceLine(summary)}
                </div>
              </div>
              <CoverageBar summary={summary} getTime={getTime} frozen={frozen} />
            </div>
          </>
        ) : mismatch ? null : (
          // The notice is suppressed under a failure block. "Nothing has been
          // checked yet ... this fills in as the video plays" is a promise the
          // page cannot keep once playback has halted, and printing it directly
          // beneath an account of a slice that disagreed reads as a
          // contradiction. The failure block is the whole message in that case.
          <div style={{ marginTop: space.md, maxWidth: '60ch', opacity: 0.85 }}>{noticeText(notice)}</div>
        )}

        {provenance && (
          <>
            <Divider />
            <Provenance {...provenance} narrow={narrow} part="summary" />
          </>
        )}

        {(summary || provenance) && (
          <div style={{ marginTop: space.md }}>
            <button
              type="button"
              onClick={() => setDetailsOpen((open) => !open)}
              aria-expanded={detailsOpen}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: color.bone,
                fontFamily: font.prose,
                fontSize: size.label,
                opacity: 0.72,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span aria-hidden="true" style={{ display: 'inline-block', width: 10 }}>
                {detailsOpen ? '−' : '+'}
              </span>
              Technical details
            </button>

            {detailsOpen && (
              <div style={{ marginTop: space.md, display: 'grid', rowGap: space.md }}>
                {summary && <SliceDetail summary={summary} getTime={getTime} frozen={frozen} narrow={narrow} />}
                {provenance && <Provenance {...provenance} narrow={narrow} part="record" />}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default VerificationModal
